/**
 * Phase L — Customer detail Invoices tab.
 *
 * Renders a per-customer aging mini-strip + filtered invoice table.
 * Backed by GET /admin/invoices?customer_id=… and
 * GET /admin/invoices/aging-summary?customer_id=…
 *
 * URL state lives in the parent page's hash query string (the tab strip
 * already drives ``?tab=invoices``); filter / search / sort are stored in
 * additional query params (``status``, ``q``, ``from``, ``to``, ``sort``).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, ArrowUpRight, AlertTriangle, Receipt, Clock, Eye } from "lucide-react";

const STATUS_CHIPS = [
    { key: "", label: "All" },
    { key: "draft", label: "Draft" },
    { key: "sent", label: "Sent" },
    { key: "viewed", label: "Viewed" },
    { key: "paid", label: "Paid" },
    { key: "overdue", label: "Overdue" },
    { key: "void", label: "Void" },
];

const STATUS_TONE = {
    draft: "bg-slate-100 text-slate-700 ring-slate-200",
    sent: "bg-blue-50 text-blue-900 ring-blue-200",
    viewed: "bg-violet-50 text-violet-800 ring-violet-200",
    paid: "bg-emerald-50 text-emerald-900 ring-emerald-200",
    overdue: "bg-red-50 text-red-700 ring-red-200",
    void: "bg-slate-100 text-slate-500 ring-slate-200",
};

// Phase STATE-DRIFT-SWEEP (2026-07-08) — unified resolver.
import { resolveInvoiceStatus } from "@/lib/statusResolvers";

const SORT_OPTIONS = [
    { key: "-issue_date", label: "Newest first" },
    { key: "issue_date", label: "Oldest first" },
    { key: "-total", label: "Largest amount" },
    { key: "total", label: "Smallest amount" },
];

function fmtMoney(cents) {
    if (cents == null) return "$0.00";
    const v = Math.abs(cents) / 100;
    return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDay(s) {
    if (!s) return "—";
    return s.slice(0, 10);
}

function fmtRel(iso) {
    if (!iso) return "—";
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const m = Math.floor(diff / 60000);
        if (m < 60) return `${Math.max(m, 1)}m ago`;
        const h = Math.floor(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.floor(h / 24);
        if (d < 30) return `${d}d ago`;
        return `${Math.floor(d / 30)}mo ago`;
    } catch {
        return iso;
    }
}

function StatusBadge({ invoice }) {
    // Phase STATE-DRIFT-SWEEP — resolver handles partial_paid + cron-lag
    // (past-due sent/viewed → Overdue) so the badge never leaks raw
    // enum text to the customer.
    const s = resolveInvoiceStatus(invoice);
    return (
        <span
            data-testid={`invoice-status-${s.code}`}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${s.tone}`}
        >
            {s.label.toUpperCase()}
        </span>
    );
}

function AgingTile({ testId, label, value, sub, href, tone = "slate" }) {
    const accent = {
        slate: "ring-slate-200 bg-white",
        red: "ring-red-200 bg-red-50/60",
        amber: "ring-amber-200 bg-amber-50/60",
        blue: "ring-blue-200 bg-blue-50/60",
    }[tone] || "ring-slate-200 bg-white";
    const inner = (
        <div data-testid={testId} className={`rounded-lg ring-1 ${accent} p-3 transition-colors hover:ring-2`}>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
            <div className="font-display text-2xl font-bold tracking-tight tabular-nums text-slate-900">{value}</div>
            {sub && <div className="text-[11px] mt-0.5 text-slate-500">{sub}</div>}
        </div>
    );
    return href ? <Link to={href}>{inner}</Link> : inner;
}

export default function CustomerInvoicesTab({ customerId, customerName }) {
    const location = useLocation();
    const navigate = useNavigate();

    // URL state — we share the parent page's search; filters live in addition
    // to the parent's ``?tab=invoices`` param.
    // Phase INV-FILTER-DEFAULT-DRAFT (2026-07-29) — default = "draft"
    // on first visit; user's last pick remembered per-surface via
    // localStorage.
    const LS_KEY = "invoices.list.customer.filter";
    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const urlStatus = params.get("status");
    const status = urlStatus ?? (localStorage.getItem(LS_KEY) ?? "draft");
    const q = params.get("q") || "";
    const from = params.get("from") || "";
    const to = params.get("to") || "";
    const sort = params.get("sort") || "-issue_date";

    const [qInput, setQInput] = useState(q);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [aging, setAging] = useState(null);
    const [agingLoading, setAgingLoading] = useState(true);

    const updateQuery = useCallback((patch) => {
        const next = new URLSearchParams(location.search);
        next.set("tab", "invoices");
        Object.entries(patch).forEach(([k, v]) => {
            if (v === "" || v == null) next.delete(k);
            else next.set(k, v);
        });
        // Phase INV-FILTER-DEFAULT-DRAFT — persist filter choice.
        if (Object.prototype.hasOwnProperty.call(patch, "status")) {
            try { localStorage.setItem(LS_KEY, patch.status || ""); } catch { /* noop */ }
        }
        navigate({ pathname: location.pathname, search: `?${next.toString()}` }, { replace: false });
    }, [location, navigate]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const reqParams = {
                    customer_id: customerId,
                    limit: 100,
                    sort,
                };
                if (status) reqParams.status = status;
                if (q) reqParams.q = q;
                if (from) reqParams.from = from;
                if (to) reqParams.to = to;
                const { data } = await api.get("/admin/invoices", { params: reqParams });
                if (!cancelled) {
                    setItems(data.items || []);
                    setTotal(data.total || 0);
                }
            } catch (err) {
                if (!cancelled) toast.error(formatApiError(err));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [customerId, status, q, from, to, sort]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setAgingLoading(true);
            try {
                const { data } = await api.get("/admin/invoices/aging-summary",
                    { params: { customer_id: customerId } });
                if (!cancelled) setAging(data);
            } catch (err) {
                if (!cancelled) console.warn("aging-summary failed", err);
            } finally {
                if (!cancelled) setAgingLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [customerId]);

    const submitSearch = (e) => {
        e?.preventDefault();
        updateQuery({ q: qInput });
    };

    const outstanding = aging?.outstanding_cents ?? 0;
    const outstandingCount = aging?.outstanding_count ?? 0;
    const overdueCount = aging?.overdue_count ?? 0;
    const due7 = aging?.due_within_7_count ?? 0;
    const past30 = aging?.past_30_count ?? 0;

    return (
        <div data-testid="customer-invoices-tab" className="space-y-5">
            {/* Aging mini-strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <AgingTile
                    testId="customer-aging-outstanding"
                    label="Outstanding"
                    value={agingLoading ? "…" : fmtMoney(outstanding)}
                    sub={`${outstandingCount} open`}
                    tone="slate"
                />
                <AgingTile
                    testId="customer-aging-overdue"
                    label="Overdue"
                    value={agingLoading ? "…" : overdueCount}
                    sub="past due date"
                    href={`${location.pathname}?tab=invoices&status=overdue`}
                    tone={overdueCount > 0 ? "red" : "slate"}
                />
                <AgingTile
                    testId="customer-aging-due7"
                    label="Due ≤7 days"
                    value={agingLoading ? "…" : due7}
                    sub="sent + viewed"
                    href={`${location.pathname}?tab=invoices&status=sent`}
                    tone={due7 > 0 ? "amber" : "slate"}
                />
                <AgingTile
                    testId="customer-aging-past30"
                    label="30+ days overdue"
                    value={agingLoading ? "…" : past30}
                    sub="stale receivables"
                    href={`${location.pathname}?tab=invoices&status=overdue`}
                    tone={past30 > 0 ? "red" : "slate"}
                />
            </div>

            {/* Filter chips */}
            <div className="flex flex-wrap items-center gap-2">
                {STATUS_CHIPS.map((c) => (
                    <button
                        key={c.key || "all"}
                        type="button"
                        data-testid={`customer-invoices-filter-${c.key || "all"}`}
                        onClick={() => updateQuery({ status: c.key })}
                        className={
                            "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 transition-colors " +
                            ((status === c.key || (!status && c.key === ""))
                                ? "bg-blue-900 text-white ring-blue-900"
                                : "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50")
                        }
                    >
                        {c.label}
                    </button>
                ))}
            </div>

            {/* Search + date range + sort */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                <form onSubmit={submitSearch} className="md:col-span-5 flex gap-2">
                    <div className="relative flex-1">
                        <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
                        <Input
                            data-testid="customer-invoices-search"
                            placeholder="Search invoice number, notes…"
                            value={qInput}
                            onChange={(e) => setQInput(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Button type="submit" variant="outline" size="sm">Search</Button>
                </form>
                <Input
                    data-testid="customer-invoices-from"
                    type="date" value={from}
                    onChange={(e) => updateQuery({ from: e.target.value })}
                    className="md:col-span-2"
                />
                <Input
                    data-testid="customer-invoices-to"
                    type="date" value={to}
                    onChange={(e) => updateQuery({ to: e.target.value })}
                    className="md:col-span-2"
                />
                <select
                    data-testid="customer-invoices-sort"
                    value={sort}
                    onChange={(e) => updateQuery({ sort: e.target.value })}
                    className="md:col-span-3 h-9 rounded-md border border-slate-200 px-3 text-sm bg-white text-slate-700"
                >
                    {SORT_OPTIONS.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    {loading && <p className="px-4 py-6 text-sm text-slate-500">Loading invoices…</p>}
                    {!loading && items.length === 0 && (
                        <p data-testid="customer-invoices-empty" className="px-4 py-12 text-center text-sm text-slate-500">
                            No invoices for this customer yet.
                        </p>
                    )}
                    {!loading && items.length > 0 && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-slate-50/60">
                                    <tr className="border-b border-slate-200">
                                        <th className="text-left px-4 py-2.5">Invoice #</th>
                                        <th className="text-left px-2 py-2.5">Status</th>
                                        <th className="text-left px-2 py-2.5">Issued</th>
                                        <th className="text-left px-2 py-2.5">Due</th>
                                        <th className="text-right px-2 py-2.5">Total</th>
                                        <th className="text-right px-2 py-2.5">Paid / Refunded</th>
                                        <th className="text-left px-2 py-2.5">Rental</th>
                                        <th className="text-left px-4 py-2.5">Viewed</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((inv) => {
                                        const paid = inv.amount_paid_cents || 0;
                                        const refunded = inv.amount_refunded_cents || 0;
                                        return (
                                            <tr
                                                key={inv.id}
                                                data-testid={`customer-invoice-row-${inv.id}`}
                                                onClick={() => navigate(`/admin/invoices/${inv.id}`)}
                                                className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 cursor-pointer"
                                            >
                                                <td className="px-4 py-3 font-medium text-slate-900">
                                                    <Link
                                                        to={`/admin/invoices/${inv.id}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="hover:text-blue-900 inline-flex items-center gap-1"
                                                    >
                                                        {inv.invoice_number}
                                                        <ArrowUpRight size={12} className="text-slate-400" />
                                                    </Link>
                                                </td>
                                                <td className="px-2 py-3"><StatusBadge invoice={inv} /></td>
                                                <td className="px-2 py-3 text-slate-600" title={inv.created_at}>{fmtDay(inv.issue_date)}</td>
                                                <td className="px-2 py-3 text-slate-600">{fmtDay(inv.due_date)}</td>
                                                <td className="px-2 py-3 text-right tabular-nums text-slate-900">{fmtMoney(inv.total_cents)}</td>
                                                <td className="px-2 py-3 text-right tabular-nums text-slate-600">
                                                    {(paid > 0 || refunded > 0) ? (
                                                        <span>
                                                            {fmtMoney(paid)} paid
                                                            {refunded > 0 && <span className="text-violet-700"> · {fmtMoney(refunded)} refunded</span>}
                                                        </span>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-2 py-3 text-slate-600">
                                                    {inv.rental_id ? (
                                                        <Link
                                                            to={`/admin/rentals/${inv.rental_id}?from=customer`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="hover:text-blue-900 inline-flex items-center gap-1"
                                                        >
                                                            Rental <ArrowUpRight size={11} className="text-slate-400" />
                                                        </Link>
                                                    ) : "—"}
                                                </td>
                                                <td className="px-4 py-3">
                                                    {inv.last_viewed_at ? (
                                                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-500" title={inv.last_viewed_at}>
                                                            <Eye size={11} /> {fmtRel(inv.last_viewed_at)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-[11px] text-slate-300">—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {!loading && total > items.length && (
                <p className="text-[11px] text-slate-500">
                    Showing first {items.length} of {total}.
                </p>
            )}
        </div>
    );
}
